// src/pages/AdminPage/tabs/UsersTab.tsx
import { useCallback, useState } from "react";
import { Users, Edit2, Trash2 } from "lucide-react";
import { adminAPI } from "../utils/adminApi";
import { usePaginatedData } from "../hooks/usePaginatedData";
import SearchBar from "../components/SearchBar";
import DataTable, { Column } from "../components/DataTable";
import Pagination from "../components/Pagination";
import type { UserItem } from "../utils/adminTypes";

interface UsersTabProps {
  onEdit: (user: UserItem) => void;
  onDelete: (userId: string) => void;
  onError: (msg: string) => void;
}

type ProviderFilter = "" | "local" | "google" | "telegram";

export default function UsersTab({ onEdit, onDelete, onError }: UsersTabProps) {
  const [provider, setProvider] = useState<ProviderFilter>("");

  const fetcher = useCallback(
    (page: number, limit: number, search: string) =>
      adminAPI.getUsers(page, limit, search, provider),
    [provider],
  );

  const {
    data: users,
    total,
    page,
    setPage,
    search,
    setSearch,
    loading,
  } = usePaginatedData<UserItem>({
    fetcher,
    limit: 10,
    onError,
  });

  const handleProviderChange = useCallback(
    (value: ProviderFilter) => {
      setProvider(value);
      setPage(1);
    },
    [setPage],
  );

  // ─── Columns ─────────────────────────────────────────────────────
  const columns: Column<UserItem>[] = [
    {
      key: "user",
      label: "Foydalanuvchi",
      render: (u) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: u.telegram_photo_url ? "transparent" : "#3B82F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 13,
              flexShrink: 0,
              overflow: "hidden",
            }}>
            {u.telegram_photo_url ?
              <img
                src={u.telegram_photo_url}
                alt={u.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            : u.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div>
            <div style={{ color: "#E2E8F0", fontWeight: 600, fontSize: 13 }}>
              {u.name || "—"}
            </div>
            <div style={{ color: "#475569", fontSize: 11 }}>
              @{u.telegram_username || "—"}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "contact",
      label: "Aloqa",
      render: (u) => (
        <div style={{ color: "#64748B", fontSize: 12 }}>
          {u.email || u.phone_number || "—"}
        </div>
      ),
    },
    {
      key: "auth",
      label: "Provayder",
      hideOnMobile: true,
      render: (u) => (
        <span
          style={{
            padding: "3px 10px",
            borderRadius: 99,
            fontSize: 11,
            fontWeight: 700,
            background:
              u.auth_provider === "telegram" ? "rgba(34,197,94,.15)"
              : u.auth_provider === "google" ? "rgba(245,158,11,.15)"
              : "rgba(100,116,139,.1)",
            color:
              u.auth_provider === "telegram" ? "#22C55E"
              : u.auth_provider === "google" ? "#F59E0B"
              : "#64748B",
          }}>
          {u.auth_provider}
        </span>
      ),
    },
    {
      key: "role",
      label: "Rol",
      render: (u) => (
        <span
          style={{
            padding: "3px 10px",
            borderRadius: 99,
            fontSize: 11,
            fontWeight: 700,
            background:
              u.role === "ADMIN" ?
                "rgba(239,68,68,.15)"
              : "rgba(59,130,246,.1)",
            color: u.role === "ADMIN" ? "#EF4444" : "#3B82F6",
          }}>
          {u.role}
        </span>
      ),
    },
    {
      key: "active",
      label: "Holat",
      render: (u) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: u.is_active ? "#22C55E" : "#64748B",
            fontSize: 12,
            fontWeight: 600,
          }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: u.is_active ? "#22C55E" : "#64748B",
            }}
          />
          {u.is_active ? "Faol" : "Nofaol"}
        </span>
      ),
    },
    {
      key: "summary",
      label: "Statistika",
      hideOnMobile: true,
      render: (u) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ color: "#E2E8F0", fontSize: 12, fontWeight: 600 }}>
            {u.summary?.totalSessions || 0} test ·{" "}
            {u.summary?.totalCorrect || 0} ✓
          </div>
          <div style={{ color: "#475569", fontSize: 11 }}>
            O'rtacha: {Number(u.summary?.avgScore || 0).toFixed(0)}%
          </div>
        </div>
      ),
    },
    {
      key: "date",
      label: "Sana",
      hideOnMobile: true,
      render: (u) => (
        <span style={{ color: "#475569", fontSize: 12 }}>
          {u.created_at ? new Date(u.created_at).toLocaleDateString("uz") : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Amallar",
      render: (u) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={() => onEdit(u)}
            title="Tahrirlash"
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: "1px solid rgba(59,130,246,.3)",
              background: "rgba(59,130,246,.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}>
            <Edit2 size={12} color="#3B82F6" />
          </button>
          <button
            onClick={() => onDelete(u.id)}
            disabled={u.role === "ADMIN"}
            title={
              u.role === "ADMIN" ? "Adminni o'chirib bo'lmaydi" : "O'chirish"
            }
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: "1px solid rgba(239,68,68,.3)",
              background: "rgba(239,68,68,.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: u.role === "ADMIN" ? "not-allowed" : "pointer",
              opacity: u.role === "ADMIN" ? 0.4 : 1,
            }}>
            <Trash2 size={12} color="#EF4444" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="fade-up">
      {/* Header */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
        }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Ism, email, telegram username..."
        />
        <select
          value={provider}
          onChange={(e) => handleProviderChange(e.target.value as ProviderFilter)}
          style={{
            padding: "10px 14px",
            borderRadius: 12,
            border: "1px solid #1E2D45",
            background: "#111827",
            color: "#E2E8F0",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}>
          <option value="">Barcha provayderlar</option>
          <option value="local">Local</option>
          <option value="google">Google</option>
          <option value="telegram">Telegram</option>
        </select>
        <div
          style={{
            color: "#64748B",
            fontSize: 13,
            padding: "10px 14px",
            background: "#111827",
            border: "1px solid #1E2D45",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
          <Users size={14} /> Jami:{" "}
          <strong style={{ color: "#fff" }}>{total}</strong>
        </div>
      </div>

      {/* Table */}
      <DataTable<UserItem>
        columns={columns}
        data={users}
        loading={loading}
        rowKey={(u) => u.id}
        minWidth={900}
        emptyMessage="Foydalanuvchilar topilmadi"
      />

      {/* Pagination */}
      <Pagination page={page} total={total} limit={10} onChange={setPage} />
    </div>
  );
}
